import { useState, useEffect, useRef } from 'react'
import {
  Play, Loader2, ChevronDown, ChevronUp, AlertCircle, CheckCircle, XCircle, MinusCircle,
  Network, Save, FolderOpen, Trash2, Link2, X, Building2, Search, User, Download,
  Star, Zap, Crown, Plus, Pencil,
} from 'lucide-react'
import { matchRateApi } from '@api/client'
import { showToast } from '@/store/uiStore'
import { useHomeStore } from '@/features/home/store'
import { useAuthStore } from '@/store/authStore'
import type { HubSpotDeal } from '@/features/home/types'
import { CountrySelect, COUNTRIES } from './CountrySelect'

// ─── Types ───────────────────────────────────────────────────────────────

interface SourceInfo {
  name: string
  vendor: string
  coverage: number | null
  consent_required: boolean
  government_id: boolean
  verifies: string[]
  mandatory_pii: string[]
  notes: string | null
  production_pass_rate?: number | null
  production_checks?: number | null
}

interface AssessmentInfo {
  achievable: boolean
  strength: number
  strength_label: string
  sources: { name: string; vendor: string; coverage: number; consent_required?: boolean }[]
  required_pii: string[]
  any_consent_free: boolean
  warnings: string[]
}

interface SpreadsheetData {
  sources: SourceInfo[]
  one_plus_one: AssessmentInfo
  two_plus_two: AssessmentInfo
  recommended_vendor: string | null
  warnings: string[]
}

interface ProductionStats {
  entity_pass_rate: number | null
  total_entities: number | null
  data_source: string | null
  best_source: string | null
  top_sources: { source: string; vendor: string; total_checks: number; fields: { field: string; pass_rate: number }[] }[]
}

interface ThoughtSpotData {
  production: ProductionStats | null
  sources: SourceInfo[]
  one_plus_one: AssessmentInfo | null
  two_plus_two: AssessmentInfo | null
  warnings: string[]
}

interface AISourceInfo {
  name: string
  vendor: string
  coverage: number | null
  consent_required: boolean
  government_id: boolean
  reason: string | null
}

interface AIStructuredResult {
  two_plus_two_achievable: boolean
  one_plus_one_achievable: boolean
  best_ruleset: '2+2' | '1+1' | 'none'
  strength: number
  strength_label: string
  sources: AISourceInfo[]
  required_pii: string[]
  warnings: string[]
  summary: string
}

interface AIResult {
  recommendation: string
  structured: AIStructuredResult | null
  status: string
  error: string | null
}

interface ExpertOverrideData {
  sources: AISourceInfo[]
  two_plus_two_achievable: boolean
  one_plus_one_achievable: boolean
  strength: number
  strength_label: string
  required_pii: string[]
  warnings: string[]
  notes: string | null
  summary: string | null
  curated_by: string | null
  updated_at: string | null
  status: string
}

interface BenchmarkData {
  id: number
  industry: string
  customer_label: string
  overall_pass_rate: number | null
  cascade_sources: { name: string; vendor?: string }[]
  notes: string | null
}

interface SourceLatencyData {
  id: number
  source_name: string
  vendor: string
  p95_min_seconds: number
  p95_max_seconds: number
}

interface CascadeResult {
  country_code: string
  country_name: string
  ruleset: string
  expert: ExpertOverrideData | null
  spreadsheet: SpreadsheetData | null
  thoughtspot: ThoughtSpotData | null
  cassidy: AIResult
  brain: AIResult
  benchmarks?: BenchmarkData[]
  latencies?: SourceLatencyData[]
  preferred?: 'expert' | 'spreadsheet' | 'production' | 'cassidy'
}

interface SavedDesign {
  id: string
  deal_id: string | null
  deal_name: string | null
  deal_company: string | null
  name: string
  countries: string[]
  ruleset: string
  sort_priority: string
  address_required: boolean
  results: CascadeResult[] | null
  created_by: string | null
  created_at: string
}

// ─── CSV Export ──────────────────────────────────────────────────────────

function csvEscape(val: string | number | null | undefined): string {
  if (val == null) return ''
  const s = String(val)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
  return s
}

function exportCascadeCsv(results: CascadeResult[]) {
  const rows: string[][] = []
  const hasAnyPreferred = results.some(r => r.preferred)

  // Header
  const header = [
    'Country', 'Country Code',
    'Data Source', '2+2 Achievable', '2+2 Strength', '1+1 Achievable', '1+1 Strength',
    'Cascade #', 'Source Name', 'Vendor', 'Coverage %', 'Consent Required', 'Gov ID', 'Required PII',
    'Prod Pass Rate', 'Prod Checks',
  ]
  if (hasAnyPreferred) header.push('Preferred')
  rows.push(header)

  for (const r of results) {
    const addExpertRows = !hasAnyPreferred || !r.preferred || r.preferred === 'expert'
    const addSheetRows = !hasAnyPreferred || !r.preferred || r.preferred === 'spreadsheet'
    const addProdRows = !hasAnyPreferred || !r.preferred || r.preferred === 'production'
    const addAiRows = !hasAnyPreferred || !r.preferred || r.preferred === 'cassidy'

    // Expert override
    if (addExpertRows && r.expert && r.expert.sources.length > 0) {
      for (let i = 0; i < r.expert.sources.length; i++) {
        const s = r.expert.sources[i]
        const row = [
          r.country_name, r.country_code,
          'Expert Override',
          r.expert.two_plus_two_achievable ? 'Yes' : 'No',
          String(r.expert.strength ?? ''),
          r.expert.one_plus_one_achievable ? 'Yes' : 'No',
          String(r.expert.strength ?? ''),
          String(i + 1), s.name, s.vendor, String(s.coverage ?? ''),
          s.consent_required ? 'Yes' : 'No',
          s.government_id ? 'Yes' : 'No',
          (r.expert.required_pii ?? []).join('; '),
          '', '',
        ]
        if (hasAnyPreferred) row.push(r.preferred === 'expert' ? 'Yes' : '')
        rows.push(row)
      }
    }

    // Spreadsheet recommendation
    if (addSheetRows) {
      const sheetAssessment = r.spreadsheet?.two_plus_two?.achievable ? r.spreadsheet.two_plus_two : r.spreadsheet?.one_plus_one
      const sheetSources = sheetAssessment?.sources ?? []
      if (sheetSources.length > 0) {
        for (let i = 0; i < sheetSources.length; i++) {
          const s = sheetSources[i]
          const full = r.spreadsheet?.sources.find(fs => fs.name === s.name && fs.vendor === s.vendor)
          const row = [
            r.country_name, r.country_code,
            'Spreadsheet',
            r.spreadsheet?.two_plus_two?.achievable ? 'Yes' : 'No',
            String(r.spreadsheet?.two_plus_two?.strength ?? ''),
            r.spreadsheet?.one_plus_one?.achievable ? 'Yes' : 'No',
            String(r.spreadsheet?.one_plus_one?.strength ?? ''),
            String(i + 1), s.name, s.vendor, String(s.coverage || ''),
            s.consent_required ? 'Yes' : 'No',
            full?.government_id ? 'Yes' : 'No',
            (full?.mandatory_pii ?? []).join('; '),
            '', '',
          ]
          if (hasAnyPreferred) row.push(r.preferred === 'spreadsheet' ? 'Yes' : '')
          rows.push(row)
        }
      } else if (!r.preferred) {
        const row = [r.country_name, r.country_code, 'Spreadsheet', 'No', '', 'No', '', '', 'No data', '', '', '', '', '', '', '']
        if (hasAnyPreferred) row.push('')
        rows.push(row)
      }
    }

    // Production recommendation
    if (addProdRows) {
      const prodAssessment = r.thoughtspot?.two_plus_two?.achievable ? r.thoughtspot.two_plus_two : r.thoughtspot?.one_plus_one
      const prodSources = prodAssessment?.sources ?? []
      if (prodSources.length > 0) {
        for (let i = 0; i < prodSources.length; i++) {
          const s = prodSources[i]
          const full = r.thoughtspot?.sources?.find(fs => fs.name === s.name && fs.vendor === s.vendor)
          const row = [
            r.country_name, r.country_code,
            'Production',
            r.thoughtspot?.two_plus_two?.achievable ? 'Yes' : 'No',
            String(r.thoughtspot?.two_plus_two?.strength ?? ''),
            r.thoughtspot?.one_plus_one?.achievable ? 'Yes' : 'No',
            String(r.thoughtspot?.one_plus_one?.strength ?? ''),
            String(i + 1), s.name, s.vendor, String(s.coverage || ''),
            s.consent_required ? 'Yes' : 'No',
            full?.government_id ? 'Yes' : 'No',
            (full?.mandatory_pii ?? []).join('; '),
            String(full?.production_pass_rate ?? ''),
            String(full?.production_checks ?? ''),
          ]
          if (hasAnyPreferred) row.push(r.preferred === 'production' ? 'Yes' : '')
          rows.push(row)
        }
      } else if (r.thoughtspot?.production && !r.preferred) {
        const row = [r.country_name, r.country_code, 'Production',
          'No', '', 'No', '',
          '', `Entity pass rate: ${r.thoughtspot.production.entity_pass_rate?.toFixed(1) ?? '?'}%`, '', '', '', '', '', '', '']
        if (hasAnyPreferred) row.push('')
        rows.push(row)
      }
    }

    // Cassidy AI recommendation
    if (addAiRows) {
      const aiStructured = r.cassidy.structured
      if (aiStructured && aiStructured.sources.length > 0) {
        for (let i = 0; i < aiStructured.sources.length; i++) {
          const s = aiStructured.sources[i]
          const row = [
            r.country_name, r.country_code,
            'Cassidy AI',
            aiStructured.two_plus_two_achievable ? 'Yes' : 'No',
            String(aiStructured.two_plus_two_achievable ? aiStructured.strength : ''),
            aiStructured.one_plus_one_achievable ? 'Yes' : 'No',
            String(aiStructured.one_plus_one_achievable ? aiStructured.strength : ''),
            String(i + 1), s.name, s.vendor, String(s.coverage ?? ''),
            s.consent_required ? 'Yes' : 'No',
            s.government_id ? 'Yes' : 'No',
            (aiStructured.required_pii ?? []).join('; '),
            '', '',
          ]
          if (hasAnyPreferred) row.push(r.preferred === 'cassidy' ? 'Yes' : '')
          rows.push(row)
        }
      }
    }
  }

  const csv = rows.map(row => row.map(csvEscape).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `cascade-design-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Component ───────────────────────────────────────────────────────────

export function CascadeDesign() {
  const { selectedDeal, selectDeal } = useHomeStore()

  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [ruleset, setRuleset] = useState<'1+1' | '2+2'>('1+1')
  const [sortPriority, setSortPriority] = useState<'coverage' | 'production'>('coverage')
  const [addressRequired, setAddressRequired] = useState(false)
  const [preferCreditHeader, setPreferCreditHeader] = useState(false)
  const [results, setResults] = useState<CascadeResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' })
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set())

  // Save/load state — use a ref for designId so autoSave always has the latest value
  const [savedDesigns, setSavedDesigns] = useState<SavedDesign[]>([])
  const [currentDesignId, _setCurrentDesignId] = useState<string | null>(null)
  const designIdRef = useRef<string | null>(null)
  const setCurrentDesignId = (id: string | null) => { designIdRef.current = id; _setCurrentDesignId(id) }
  const [currentDesignName, setCurrentDesignName] = useState<string>('')
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [showSaved, setShowSaved] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [editingExpertCountry, setEditingExpertCountry] = useState<string | null>(null)

  // Load saved designs on mount and when deal changes — auto-load most recent if deal has saved designs
  useEffect(() => {
    const load = async () => {
      try {
        const res = await matchRateApi.listCascadeDesigns(selectedDeal?.id || undefined)
        const data = (res as any)?.data?.data || (res as any)?.data
        if (Array.isArray(data)) {
          const designs = data as SavedDesign[]
          setSavedDesigns(designs)

          // Auto-load the most recent design for this deal
          if (selectedDeal?.id && designs.length > 0) {
            const latest = designs[0] // already sorted by updated_at DESC
            setSelectedCountries(latest.countries)
            setRuleset(latest.ruleset as '1+1' | '2+2')
            setSortPriority((latest.sort_priority as 'coverage' | 'production') || 'coverage')
            setAddressRequired(latest.address_required)
            setCurrentDesignId(latest.id)
            setCurrentDesignName(latest.name)
            if (latest.results && latest.results.length > 0) {
              setResults(latest.results)
              setExpandedCountries(new Set(latest.results.map(r => r.country_code)))
            }
            showToast(`Loaded "${latest.name}" for ${selectedDeal.name}`, 'success')
          }
        }
      } catch { /* ignore */ }
    }
    load()
  }, [selectedDeal?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (code: string) => {
    setExpandedCountries(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  // Auto-save helper — silently saves current state without toasts
  const autoSave = async (resultsToSave: CascadeResult[]) => {
    const id = designIdRef.current
    const name = currentDesignName || (selectedDeal?.name ? `${selectedDeal.name} Cascade` : `Cascade ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`)
    try {
      const res = await matchRateApi.saveCascadeDesign({
        id: id || undefined,
        name,
        deal_id: selectedDeal?.id || null,
        deal_name: selectedDeal?.name || null,
        deal_company: selectedDeal?.companyName || null,
        countries: selectedCountries,
        ruleset,
        sort_priority: sortPriority,
        address_required: addressRequired,
        results: resultsToSave,
      })
      const payload = (res as any)?.data?.data || (res as any)?.data
      if (payload?.id) {
        setCurrentDesignId(payload.id)
        setCurrentDesignName(name)
      }
    } catch { /* silent */ }
  }

  const handleRun = async () => {
    if (selectedCountries.length === 0) {
      showToast('Select at least one country', 'error')
      return
    }
    setLoading(true)
    setResults(null)
    setProgress({ done: 0, total: selectedCountries.length, current: '' })

    const allResults: CascadeResult[] = []
    let failed = 0

    for (let i = 0; i < selectedCountries.length; i++) {
      const code = selectedCountries[i]
      const countryName = COUNTRIES.find(c => c.code === code)?.name || code
      setProgress({ done: i, total: selectedCountries.length, current: countryName })

      try {
        const res = await matchRateApi.cascadeDesign({
          country_codes: [code],
          ruleset,
          sort_priority: sortPriority,
          address_required: addressRequired,
          prefer_credit_header: preferCreditHeader,
          deal_id: selectedDeal?.id || null,
        })
        const data = (res as any)?.data?.data || (res as any)?.data
        if (Array.isArray(data)) {
          allResults.push(...(data as CascadeResult[]))
          setResults([...allResults])
          setExpandedCountries(new Set(allResults.map(r => r.country_code)))
          // Auto-save after each country completes
          autoSave(allResults)
        }
      } catch (err) {
        console.error(`Cascade design failed for ${code}:`, err)
        failed++
      }
    }

    if (failed > 0) {
      showToast(`${failed} countr${failed === 1 ? 'y' : 'ies'} failed`, 'error')
    }
    setProgress({ done: selectedCountries.length, total: selectedCountries.length, current: '' })
    setLoading(false)
    // Refresh saved designs list
    try {
      const listRes = await matchRateApi.listCascadeDesigns(selectedDeal?.id || undefined)
      const data = (listRes as any)?.data?.data || (listRes as any)?.data
      if (Array.isArray(data)) setSavedDesigns(data as SavedDesign[])
    } catch { /* ignore */ }
  }

  const handleSave = async () => {
    const nameToUse = saveName.trim() || currentDesignName || (selectedDeal?.name ? `${selectedDeal.name} Cascade` : `Cascade ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`)
    if (selectedCountries.length === 0) return
    try {
      const res = await matchRateApi.saveCascadeDesign({
        id: currentDesignId || undefined,
        name: nameToUse,
        deal_id: selectedDeal?.id || null,
        deal_name: selectedDeal?.name || null,
        deal_company: selectedDeal?.companyName || null,
        countries: selectedCountries,
        ruleset,
        sort_priority: sortPriority,
        address_required: addressRequired,
        results: results || undefined,
      })
      const payload = (res as any)?.data?.data || (res as any)?.data
      if (payload?.id) {
        setCurrentDesignId(payload.id)
        setCurrentDesignName(nameToUse)
        showToast(`${currentDesignId ? 'Updated' : 'Saved'} "${nameToUse}"${results ? ' with results' : ''}`, 'success')
        setSaveName('')
        setShowSaveDialog(false)
        // Refresh list
        const listRes = await matchRateApi.listCascadeDesigns(selectedDeal?.id || undefined)
        const data = (listRes as any)?.data?.data || (listRes as any)?.data
        if (Array.isArray(data)) setSavedDesigns(data as SavedDesign[])
      } else {
        showToast('Failed to save', 'error')
      }
    } catch {
      showToast('Failed to save cascade design', 'error')
    }
  }

  const handleLoad = (design: SavedDesign) => {
    // Restore deal if the design was linked to one
    if (design.deal_id && design.deal_name) {
      selectDeal({
        id: design.deal_id,
        name: design.deal_name,
        companyName: design.deal_company || null,
        amount: null,
        stage: '',
        closeDate: null,
        ownerName: '',
        createdAt: '',
      } as HubSpotDeal)
    }
    setSelectedCountries(design.countries)
    setRuleset(design.ruleset as '1+1' | '2+2')
    setSortPriority((design.sort_priority as 'coverage' | 'production') || 'coverage')
    setAddressRequired(design.address_required)
    setCurrentDesignId(design.id)
    setCurrentDesignName(design.name)
    if (design.results && design.results.length > 0) {
      setResults(design.results)
      setExpandedCountries(new Set(design.results.map(r => r.country_code)))
      showToast(`Loaded "${design.name}" with saved results`, 'success')
    } else {
      showToast(`Loaded "${design.name}"`, 'success')
    }
    setShowSaved(false)
  }

  const handleDelete = async (id: string) => {
    try {
      await matchRateApi.deleteCascadeDesign(id)
      setSavedDesigns(prev => prev.filter(d => d.id !== id))
    } catch {
      showToast('Failed to delete', 'error')
    }
  }

  const handleShare = async () => {
    if (selectedCountries.length === 0) {
      showToast('Select countries first', 'error')
      return
    }
    setSharing(true)
    try {
      const name = saveName.trim() || `Cascade ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`
      const res = await matchRateApi.saveCascadeDesign({
        name,
        deal_id: selectedDeal?.id || null,
        deal_name: selectedDeal?.name || null,
        deal_company: selectedDeal?.companyName || null,
        countries: selectedCountries,
        ruleset,
        sort_priority: sortPriority,
        address_required: addressRequired,
        results: results || undefined,
      })
      const payload = (res as any)?.data?.data || (res as any)?.data
      if (payload?.id) {
        const shareUrl = `${window.location.origin}/dashboard/presales/tools/match-rate?cascade=${payload.id}`
        await navigator.clipboard.writeText(shareUrl)
        showToast('Link copied to clipboard', 'success')
      } else {
        showToast('Failed to share', 'error')
      }
    } catch {
      showToast('Failed to share', 'error')
    }
    setSharing(false)
  }

  return (
    <div className="space-y-4">
      {/* Setup Panel */}
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Network size={16} className="text-violet-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Cascade Design</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Load saved */}
            <div className="relative">
              <button
                onClick={() => setShowSaved(!showSaved)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors border border-[var(--border-color)]"
              >
                <FolderOpen size={12} /> Saved ({savedDesigns.length})
              </button>
              {showSaved && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSaved(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-80 max-h-64 overflow-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-xl">
                    {savedDesigns.length === 0 ? (
                      <div className="px-4 py-6 text-center text-xs text-[var(--text-muted)]">
                        No saved cascade designs yet
                      </div>
                    ) : (
                      savedDesigns.map(d => (
                        <div key={d.id} className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border-color)]/50 hover:bg-[var(--bg-tertiary)]/50 group">
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleLoad(d)}>
                            <div className="text-xs font-medium text-[var(--text-primary)] truncate">{d.name}</div>
                            {d.deal_name && (
                              <div className="text-[10px] text-violet-400 flex items-center gap-1 mt-0.5">
                                <Building2 size={9} /> {d.deal_name}
                              </div>
                            )}
                            <div className="text-[10px] text-[var(--text-muted)]">
                              {d.countries.length} countries &bull; {d.ruleset} &bull; {d.sort_priority === 'production' ? 'Prod' : 'Coverage'}
                              {d.address_required ? ' &bull; Addr' : ''}
                              {' &bull; '}{new Date(d.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </div>
                            {d.results && d.results.length > 0 && (
                              <div className="text-[10px] text-emerald-400">{d.results.length} saved results</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDelete(d.id)}
                            className="p-1 rounded hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Save */}
            <button
              onClick={() => {
                if (selectedCountries.length === 0) { showToast('Select countries first', 'error'); return }
                if (currentDesignId) {
                  // Update existing design directly
                  handleSave()
                } else {
                  setShowSaveDialog(true)
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors border border-[var(--border-color)]"
            >
              <Save size={12} /> {currentDesignId ? 'Save' : 'Save As'}
            </button>
            {/* Share */}
            <button
              onClick={handleShare}
              disabled={sharing || selectedCountries.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
            >
              <Link2 size={12} /> {sharing ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </div>

        {/* Save dialog */}
        {showSaveDialog && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-violet-500/5 border border-violet-500/20">
            <input
              autoFocus
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setShowSaveDialog(false) }}
              placeholder="Design name (e.g. CBA APAC Cascade)"
              className="flex-1 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/50"
            />
            <button
              onClick={handleSave}
              disabled={!saveName.trim()}
              className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => { setShowSaveDialog(false); setSaveName('') }}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Deal */}
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Deal</label>
          <DealPicker />
        </div>

        {/* Country select */}
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Countries</label>
          <CountrySelect selected={selectedCountries} onChange={setSelectedCountries} />
          {selectedCountries.length > 0 && (
            <div className="text-[10px] text-[var(--text-muted)] mt-1">{selectedCountries.length} selected</div>
          )}
        </div>

        {/* Settings + Run */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Ruleset</label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
              <button
                onClick={() => setRuleset('1+1')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  ruleset === '1+1' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                1+1
              </button>
              <button
                onClick={() => setRuleset('2+2')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-[var(--border-color)] ${
                  ruleset === '2+2' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                2+2
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Rank by</label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
              <button
                onClick={() => setSortPriority('coverage')}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  sortPriority === 'coverage' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
                title="Rank sources by adult population coverage from the internal KYC sheet"
              >
                Coverage
              </button>
              <button
                onClick={() => setSortPriority('production')}
                className={`px-3 py-2 text-xs font-medium transition-colors border-l border-[var(--border-color)] ${
                  sortPriority === 'production' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
                title="Rank sources by real pass rate from FrankieOne production data (ThoughtSpot)"
              >
                Prod. Data
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pb-0.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addressRequired}
                onChange={e => setAddressRequired(e.target.checked)}
                className="accent-violet-500"
              />
              <span className="text-xs text-[var(--text-secondary)]">Address validation required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={preferCreditHeader}
                onChange={e => setPreferCreditHeader(e.target.checked)}
                className="accent-violet-500"
              />
              <span className="text-xs text-[var(--text-secondary)]">Prefer credit header sources</span>
            </label>
          </div>

          <button
            id="cascade-run-btn"
            onClick={handleRun}
            disabled={loading || selectedCountries.length === 0}
            className="flex items-center gap-2 px-5 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Processing {selectedCountries.length} countries...</>
            ) : (
              <><Play size={14} /> Generate Cascade Design</>
            )}
          </button>
        </div>
      </div>

      {/* Progress */}
      {loading && (
        <div className="flex flex-col items-center py-8 gap-3">
          <Loader2 size={24} className="animate-spin text-violet-400" />
          <p className="text-sm text-[var(--text-muted)]">
            Processing {progress.current || '...'}  (Cassidy + Brain calls may take 1-3 min each)
          </p>
          <div className="w-64 bg-[var(--bg-tertiary)] rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-violet-500 transition-all duration-300 rounded-full"
              style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            {progress.done} / {progress.total} countries
          </p>
        </div>
      )}

      {/* Results */}
      {results && results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Results — {results.length} {results.length === 1 ? 'country' : 'countries'}
            </h3>
            <button
              onClick={() => exportCascadeCsv(results)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors border border-[var(--border-color)]"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>

          {results.map(r => (
            <CountryResult
              key={r.country_code}
              result={r}
              expanded={expandedCountries.has(r.country_code)}
              onToggle={() => toggleExpanded(r.country_code)}
              onSetPreferred={(tab) => {
                const updated = results.map(res =>
                  res.country_code === r.country_code ? { ...res, preferred: res.preferred === tab ? undefined : tab } : res
                )
                setResults(updated)
                autoSave(updated)
              }}
              onEditExpert={(cc) => setEditingExpertCountry(cc)}
            />
          ))}
        </div>
      )}

      {/* Expert Override Editor Modal */}
      {editingExpertCountry && (() => {
        const cr = results?.find(r => r.country_code === editingExpertCountry)
        // Merge all available sources from spreadsheet + production, deduped
        const allSources: AvailableSource[] = []
        const seen = new Set<string>()
        const addSrc = (s: SourceInfo) => {
          const key = `${s.name}|${s.vendor}`
          if (seen.has(key)) return
          seen.add(key)
          allSources.push({
            name: s.name, vendor: s.vendor, coverage: s.coverage,
            consent_required: s.consent_required, government_id: s.government_id,
            production_pass_rate: s.production_pass_rate, production_checks: s.production_checks,
          })
        }
        for (const s of cr?.spreadsheet?.sources ?? []) addSrc(s)
        for (const s of cr?.thoughtspot?.sources ?? []) addSrc(s)
        // Also add AI-recommended sources that might not be in the other lists
        if (cr?.cassidy.structured?.sources) {
          for (const s of cr.cassidy.structured.sources) {
            const key = `${s.name}|${s.vendor}`
            if (!seen.has(key)) {
              seen.add(key)
              allSources.push({ name: s.name, vendor: s.vendor, coverage: s.coverage, consent_required: s.consent_required, government_id: s.government_id })
            }
          }
        }
        return (
          <ExpertOverrideEditor
            countryCode={editingExpertCountry}
            ruleset={ruleset}
            existingData={cr?.expert ?? null}
            availableSources={allSources}
            onClose={() => setEditingExpertCountry(null)}
            onSaved={(savedExpert) => {
              // Inject the saved expert override into results immediately so the UI updates
              if (results) {
                const updated = results.map(r =>
                  r.country_code === editingExpertCountry
                    ? { ...r, expert: savedExpert, preferred: r.preferred || 'expert' }
                    : r
                )
                setResults(updated)
                autoSave(updated)
              }
            }}
          />
        )
      })()}
    </div>
  )
}

// ─── Country Result Card ─────────────────────────────────────────────────

type RecommendationTab = 'expert' | 'spreadsheet' | 'production' | 'cassidy'

function aiStructuredToAssessment(ai: AIStructuredResult, ruleset: '1+1' | '2+2'): AssessmentInfo | null {
  const achievable = ruleset === '2+2' ? ai.two_plus_two_achievable : ai.one_plus_one_achievable
  if (!achievable) return null
  return {
    achievable: true,
    strength: ai.strength,
    strength_label: ai.strength_label,
    sources: ai.sources.map(s => ({
      name: s.name,
      vendor: s.vendor,
      coverage: s.coverage ?? 0,
      consent_required: s.consent_required,
    })),
    required_pii: ai.required_pii,
    any_consent_free: ai.sources.some(s => !s.consent_required),
    warnings: ai.warnings,
  }
}

function aiSourcesToSourceInfo(sources: AISourceInfo[]): SourceInfo[] {
  return sources.map(s => ({
    name: s.name,
    vendor: s.vendor,
    coverage: s.coverage,
    consent_required: s.consent_required,
    government_id: s.government_id,
    verifies: [],
    mandatory_pii: [],
    notes: s.reason,
  }))
}

/** 2+2 is only green if strength > 60%. Otherwise amber. 1+1 stays blue. */
function rulesetBadgeColor(ruleset: string, strength: number | null): string {
  if (ruleset === '1+1') return 'bg-blue-500/10 text-blue-400'
  // 2+2: green only when strong (>60%), otherwise amber
  if (strength != null && strength > 60) return 'bg-green-500/10 text-green-400'
  return 'bg-amber-500/10 text-amber-400'
}

function CountryResult({ result, expanded, onToggle, onSetPreferred, onEditExpert }: { result: CascadeResult; expanded: boolean; onToggle: () => void; onSetPreferred: (tab: RecommendationTab) => void; onEditExpert: (countryCode: string) => void }) {
  const r = result
  const hasExpert = !!r.expert
  const [activeTab, setActiveTab] = useState<RecommendationTab>(hasExpert ? 'expert' : 'spreadsheet')

  // Determine best achievable ruleset for each source (safely handle old saved data shapes)
  const expertRuleset = r.expert
    ? (r.expert.two_plus_two_achievable ? '2+2' : r.expert.one_plus_one_achievable ? '1+1' : null)
    : null
  const sheetRuleset = r.spreadsheet
    ? (r.spreadsheet.two_plus_two?.achievable ? '2+2' : r.spreadsheet.one_plus_one?.achievable ? '1+1' : null)
    : null
  const prodRuleset = r.thoughtspot
    ? (r.thoughtspot.two_plus_two?.achievable ? '2+2' : r.thoughtspot.one_plus_one?.achievable ? '1+1' : null)
    : null
  const cassidyStructured = r.cassidy.structured
  const cassidyRuleset = cassidyStructured
    ? (cassidyStructured.best_ruleset === '2+2' ? '2+2' : cassidyStructured.best_ruleset === '1+1' ? '1+1' : null)
    : null

  const expertStatus = r.expert ? 'success' : 'missing'
  const spreadsheetStatus = r.spreadsheet ? (sheetRuleset ? 'success' : 'warning') : 'missing'
  const tsStatus = r.thoughtspot ? (prodRuleset ? 'success' : r.thoughtspot.production ? 'warning' : 'missing') : 'missing'
  const cassidyStatus = r.cassidy.status
  const brainStatus = r.brain.status

  // Get strength for each source to determine color
  const expertStrength = r.expert?.strength ?? null
  const sheetStrength = r.spreadsheet
    ? (r.spreadsheet.two_plus_two?.achievable ? r.spreadsheet.two_plus_two.strength : r.spreadsheet.one_plus_one?.achievable ? r.spreadsheet.one_plus_one.strength : null)
    : null
  const prodStrength = r.thoughtspot
    ? (r.thoughtspot.two_plus_two?.achievable ? r.thoughtspot.two_plus_two.strength : r.thoughtspot.one_plus_one?.achievable ? r.thoughtspot.one_plus_one.strength : null)
    : null
  const cassidyStrength = cassidyStructured
    ? (cassidyStructured.two_plus_two_achievable || cassidyStructured.one_plus_one_achievable ? cassidyStructured.strength : null)
    : null

  const tabs: { key: RecommendationTab; label: string; shortLabel: string; ruleset: string | null; strength: number | null; status: string; isExpert?: boolean }[] = [
    ...(hasExpert ? [{ key: 'expert' as RecommendationTab, label: 'Expert Override', shortLabel: 'Expert', ruleset: expertRuleset, strength: expertStrength, status: expertStatus, isExpert: true }] : []),
    { key: 'spreadsheet', label: 'D1 Spreadsheet', shortLabel: 'Sheet', ruleset: sheetRuleset, strength: sheetStrength, status: spreadsheetStatus },
    { key: 'production', label: 'Production Data', shortLabel: 'Prod', ruleset: prodRuleset, strength: prodStrength, status: tsStatus },
    { key: 'cassidy', label: 'Cassidy AI', shortLabel: 'Cassidy', ruleset: cassidyRuleset, strength: cassidyStrength, status: cassidyStatus },
  ]

  return (
    <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[var(--text-primary)]">
            {r.country_name} ({r.country_code})
          </span>
          <div className="flex items-center gap-2 ml-1">
            {hasExpert && expertRuleset && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium bg-amber-500/15 text-amber-400 border border-amber-500/25 flex items-center gap-0.5`}>
                <Crown size={8} /> Expert: {expertRuleset}
              </span>
            )}
            {sheetRuleset && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${rulesetBadgeColor(sheetRuleset, sheetStrength)}`}>
                Sheet: {sheetRuleset}
              </span>
            )}
            {prodRuleset && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${rulesetBadgeColor(prodRuleset, prodStrength)}`}>
                Prod: {prodRuleset}
              </span>
            )}
            {cassidyRuleset && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${rulesetBadgeColor(cassidyRuleset, cassidyStrength)}`}>
                AI: {cassidyRuleset}
              </span>
            )}
          </div>
          {r.preferred && (
            <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-violet-500/15 text-violet-400 border border-violet-500/30 flex items-center gap-1">
              <CheckCircle size={9} />
              {r.preferred === 'expert' ? 'Expert' : r.preferred === 'spreadsheet' ? 'Sheet' : r.preferred === 'production' ? 'Prod' : 'AI'}
            </span>
          )}
          <div className="flex items-center gap-2 ml-2">
            <StatusDot label="Sheet" status={spreadsheetStatus} />
            <StatusDot label="Prod" status={tsStatus} />
            <StatusDot label="Cassidy" status={cassidyStatus} />
            <StatusDot label="Brain" status={brainStatus} />
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-[var(--text-muted)]" /> : <ChevronDown size={16} className="text-[var(--text-muted)]" />}
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-color)] p-4 space-y-4">
          {/* Recommendation tab selector */}
          <div className="flex items-center gap-1 bg-[var(--bg-primary)] rounded-lg p-1 border border-[var(--border-color)]">
            {tabs.map(tab => {
              const isPreferred = r.preferred === tab.key
              const isExpert = tab.isExpert
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors flex-1 justify-center ${
                    activeTab === tab.key
                      ? isExpert
                        ? 'bg-amber-600 text-white font-medium shadow-sm'
                        : isPreferred
                          ? 'bg-violet-600 text-white font-medium shadow-sm ring-2 ring-violet-400/50'
                          : 'bg-violet-600 text-white font-medium shadow-sm'
                      : isExpert
                        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30 font-medium'
                        : isPreferred
                          ? 'bg-violet-500/10 text-violet-400 border border-violet-500/30 font-medium'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                  }`}
                >
                  {isExpert ? (
                    <Crown size={10} className={`shrink-0 ${activeTab === tab.key ? 'text-white' : 'text-amber-400'}`} />
                  ) : isPreferred ? (
                    <Star size={10} className={`shrink-0 ${activeTab === tab.key ? 'text-yellow-300 fill-yellow-300' : 'text-violet-400 fill-violet-400'}`} />
                  ) : (
                    <StatusDot label="" status={tab.status} />
                  )}
                  <span>{tab.label}</span>
                  {isPreferred && !isExpert && activeTab !== tab.key && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-violet-500/20 text-violet-300 font-medium">Preferred</span>
                  )}
                  {tab.ruleset && (
                    <span className={`px-1 py-0.5 text-[9px] rounded font-medium ${
                      activeTab === tab.key
                        ? 'bg-white/20 text-white'
                        : rulesetBadgeColor(tab.ruleset, tab.strength)
                    }`}>
                      {tab.ruleset}
                    </span>
                  )}
                </button>
              )
            })}
            {/* Create expert override button (when none exists) */}
            {!hasExpert && (
              <button
                onClick={() => onEditExpert(r.country_code)}
                className="flex items-center gap-1 px-2 py-1.5 text-[10px] rounded-md text-amber-400 hover:bg-amber-500/10 transition-colors shrink-0"
                title="Create expert override for this country"
              >
                <Crown size={10} /> <Plus size={8} />
              </button>
            )}
          </div>

          {/* Active recommendation panel */}
          {activeTab === 'expert' && r.expert && (
            <>
              <ExpertPanel expert={r.expert} countryCode={r.country_code} onEdit={() => onEditExpert(r.country_code)} />
              <PreferButton tab="expert" preferred={r.preferred} onSetPreferred={onSetPreferred} />
            </>
          )}

          {activeTab === 'spreadsheet' && (
            <>
              <CascadePanel
                title="D1 Spreadsheet Recommendation"
                subtitle="Based on internal coverage estimates"
                assessment11={r.spreadsheet?.one_plus_one ?? null}
                assessment22={r.spreadsheet?.two_plus_two ?? null}
                sources={r.spreadsheet?.sources ?? []}
                warnings={r.spreadsheet?.warnings ?? []}
                recommendedVendor={r.spreadsheet?.recommended_vendor ?? null}
                status={spreadsheetStatus}
              />
              <PreferButton tab="spreadsheet" preferred={r.preferred} onSetPreferred={onSetPreferred} />
            </>
          )}

          {activeTab === 'production' && (
            <>
              <CascadePanel
                title="Production Data Recommendation"
                subtitle={r.thoughtspot?.production ? `${r.thoughtspot.production.entity_pass_rate?.toFixed(1) ?? '?'}% entity pass rate · ${(r.thoughtspot.production.total_entities ?? 0).toLocaleString()} entities · ${r.thoughtspot.production.data_source === 'benchmark' ? 'Benchmark (Kraken)' : 'All customers'}` : 'No production data'}
                assessment11={r.thoughtspot?.one_plus_one ?? null}
                assessment22={r.thoughtspot?.two_plus_two ?? null}
                sources={r.thoughtspot?.sources ?? []}
                warnings={r.thoughtspot?.warnings ?? []}
                recommendedVendor={null}
                status={tsStatus}
                showProductionStats
              />
              <PreferButton tab="production" preferred={r.preferred} onSetPreferred={onSetPreferred} />
            </>
          )}

          {activeTab === 'cassidy' && (
            <>
              {cassidyStructured ? (
                <CascadePanel
                  title="Cassidy AI Recommendation"
                  subtitle={cassidyStructured.summary || 'AI-generated cascade recommendation'}
                  assessment11={aiStructuredToAssessment(cassidyStructured, '1+1')}
                  assessment22={aiStructuredToAssessment(cassidyStructured, '2+2')}
                  sources={aiSourcesToSourceInfo(cassidyStructured.sources)}
                  warnings={cassidyStructured.warnings}
                  recommendedVendor={null}
                  status={cassidyStatus}
                />
              ) : (
                <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] p-3">
                  <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">Cassidy AI</h4>
                  {r.cassidy.status === 'success' ? (
                    <div className="text-xs text-[var(--text-primary)] whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed">
                      {r.cassidy.recommendation}
                    </div>
                  ) : (
                    <p className="text-xs text-red-400">{r.cassidy.error || 'No response'}</p>
                  )}
                </div>
              )}
              <PreferButton tab="cassidy" preferred={r.preferred} onSetPreferred={onSetPreferred} />
            </>
          )}

          {/* Benchmarks & Latency (shown below recommendation panels) */}
          <BenchmarksPanel
            benchmarks={r.benchmarks || []}
            latencies={r.latencies || []}
            countryCode={r.country_code}
            activeSources={getActiveSources(r, activeTab)}
          />

          {/* Brain text (collapsible, always shown below the main panel) */}
          <BrainSection brain={r.brain} />
        </div>
      )}
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Get source names from the currently active tab's recommendation */
function getActiveSources(r: CascadeResult, activeTab: RecommendationTab): string[] {
  if (activeTab === 'expert' && r.expert) return r.expert.sources.map(s => s.name)
  if (activeTab === 'spreadsheet' && r.spreadsheet) return r.spreadsheet.sources.map(s => s.name)
  if (activeTab === 'production' && r.thoughtspot) return r.thoughtspot.sources.map(s => s.name)
  if (activeTab === 'cassidy' && r.cassidy.structured) return r.cassidy.structured.sources.map(s => s.name)
  return []
}

// ─── Benchmarks & Latency Panel ──────────────────────────────────────

function BenchmarksPanel({ benchmarks, latencies, countryCode, activeSources }: {
  benchmarks: BenchmarkData[]
  latencies: SourceLatencyData[]
  countryCode: string
  activeSources: string[]
}) {
  const [showEditor, setShowEditor] = useState(false)
  const [showLatencyEditor, setShowLatencyEditor] = useState(false)
  const { user } = useAuthStore()
  const isAdmin = user?.isAdmin

  if (benchmarks.length === 0 && latencies.length === 0 && !isAdmin) return null

  // Compute cumulative P95 for the active cascade based on latency data
  const cumulativeP95: { name: string; cumMin: number; cumMax: number }[] = []
  if (activeSources.length > 0 && latencies.length > 0) {
    let cumMin = 0, cumMax = 0
    for (const srcName of activeSources) {
      const lat = latencies.find(l => l.source_name.toLowerCase() === srcName.toLowerCase())
      if (lat) {
        cumMin += lat.p95_min_seconds
        cumMax += lat.p95_max_seconds
        cumulativeP95.push({ name: srcName, cumMin: Math.round(cumMin * 10) / 10, cumMax: Math.round(cumMax * 10) / 10 })
      }
    }
  }

  return (
    <div className="space-y-2">
      {/* Customer Benchmarks */}
      {(benchmarks.length > 0 || isAdmin) && (
        <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
              <Building2 size={11} />
              Customer Benchmarks
            </h4>
            {isAdmin && (
              <button
                onClick={() => setShowEditor(true)}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <Plus size={9} /> Add
              </button>
            )}
          </div>
          {benchmarks.length > 0 ? (
            <div className="space-y-1.5">
              {benchmarks.map(b => (
                <BenchmarkRow key={b.id} benchmark={b} countryCode={countryCode} isAdmin={!!isAdmin} onDeleted={() => window.location.reload()} />
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-[var(--text-muted)]">No customer benchmarks yet for {countryCode}</p>
          )}
        </div>
      )}

      {/* Cumulative P95 Exit Timing */}
      {cumulativeP95.length > 0 && (
        <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] p-3">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <Zap size={11} />
            Cumulative P95 by Exit Point
          </h4>
          <div className="space-y-1">
            {cumulativeP95.map((step, i) => {
              const maxTime = cumulativeP95[cumulativeP95.length - 1].cumMax
              const barWidth = maxTime > 0 ? (step.cumMax / maxTime) * 100 : 0
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--text-secondary)] w-28 shrink-0 truncate" title={step.name}>
                    Exit @ {step.name}
                  </span>
                  <div className="flex-1 h-4 bg-[var(--bg-tertiary)] rounded-full overflow-hidden relative">
                    <div
                      className="h-full bg-gradient-to-r from-green-500/40 to-amber-500/40 rounded-full transition-all"
                      style={{ width: `${barWidth}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-medium text-[var(--text-primary)]">
                      {step.cumMin}–{step.cumMax}s
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Source Latency Table */}
      {(latencies.length > 0 || isAdmin) && (
        <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
              <Zap size={11} />
              Source Response Times (P95)
            </h4>
            {isAdmin && (
              <button
                onClick={() => setShowLatencyEditor(true)}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <Plus size={9} /> Add
              </button>
            )}
          </div>
          {latencies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {latencies.map(l => (
                <div key={l.id} className="flex items-center justify-between px-2 py-1 rounded bg-[var(--bg-tertiary)] text-[10px]">
                  <span className="text-[var(--text-secondary)] truncate mr-1">{l.source_name}</span>
                  <span className="text-[var(--text-primary)] font-medium shrink-0">{l.p95_min_seconds}–{l.p95_max_seconds}s</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-[var(--text-muted)]">No latency data yet</p>
          )}
        </div>
      )}

      {/* Benchmark Editor Modal */}
      {showEditor && (
        <BenchmarkEditor countryCode={countryCode} onClose={() => setShowEditor(false)} />
      )}

      {/* Latency Editor Modal */}
      {showLatencyEditor && (
        <LatencyEditor countryCode={countryCode} onClose={() => setShowLatencyEditor(false)} />
      )}
    </div>
  )
}

function BenchmarkRow({ benchmark: b, countryCode: _cc, isAdmin, onDeleted }: {
  benchmark: BenchmarkData; countryCode: string; isAdmin: boolean; onDeleted: () => void
}) {
  const handleDelete = async () => {
    if (!confirm(`Delete benchmark "${b.customer_label}"?`)) return
    await matchRateApi.deleteBenchmark(b.id)
    showToast('Benchmark deleted', 'success')
    onDeleted()
  }

  return (
    <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-[var(--bg-tertiary)] group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-[var(--text-primary)]">{b.customer_label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">{b.industry}</span>
          {b.overall_pass_rate != null && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              b.overall_pass_rate >= 80 ? 'bg-green-500/10 text-green-400' :
              b.overall_pass_rate >= 60 ? 'bg-amber-500/10 text-amber-400' :
              'bg-red-500/10 text-red-400'
            }`}>
              {b.overall_pass_rate}% pass rate
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {b.cascade_sources.map((s, i) => (
            <span key={i} className="text-[10px] text-[var(--text-secondary)]">
              {i > 0 && <span className="text-[var(--text-muted)] mx-0.5">→</span>}
              {s.name}
            </span>
          ))}
        </div>
        {b.notes && (
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{b.notes}</p>
        )}
      </div>
      {isAdmin && (
        <button onClick={handleDelete} className="p-1 text-[var(--text-muted)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Trash2 size={11} />
        </button>
      )}
    </div>
  )
}

// ─── Benchmark Editor Modal ──────────────────────────────────────────

function BenchmarkEditor({ countryCode, onClose }: { countryCode: string; onClose: () => void }) {
  const [industry, setIndustry] = useState('')
  const [customerLabel, setCustomerLabel] = useState('')
  const [passRate, setPassRate] = useState('')
  const [cascadeSources, setCascadeSources] = useState<{ name: string; vendor: string }[]>([])
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [srcName, setSrcName] = useState('')
  const [srcVendor, setSrcVendor] = useState('')

  const addSource = () => {
    if (!srcName.trim()) return
    setCascadeSources(prev => [...prev, { name: srcName.trim(), vendor: srcVendor.trim() }])
    setSrcName('')
    setSrcVendor('')
  }

  const handleSave = async () => {
    if (!industry.trim() || !customerLabel.trim()) {
      showToast('Industry and customer label required', 'error')
      return
    }
    setSaving(true)
    try {
      await matchRateApi.saveBenchmark({
        country_code: countryCode,
        industry: industry.trim(),
        customer_label: customerLabel.trim(),
        overall_pass_rate: passRate ? parseFloat(passRate) : null,
        cascade_sources: cascadeSources,
        notes: notes.trim() || null,
      })
      showToast('Benchmark saved', 'success')
      onClose()
      window.location.reload()
    } catch {
      showToast('Failed to save benchmark', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto mx-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-blue-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Add Customer Benchmark — {countryCode}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-muted)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Industry</label>
              <input
                value={industry}
                onChange={e => setIndustry(e.target.value)}
                placeholder="e.g. Banking, Wagering"
                className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Customer Label</label>
              <input
                value={customerLabel}
                onChange={e => setCustomerLabel(e.target.value)}
                placeholder="e.g. Major Bank"
                className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Overall Pass Rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={passRate}
              onChange={e => setPassRate(e.target.value)}
              placeholder="e.g. 94.1"
              className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">
              Cascade Sources ({cascadeSources.length})
            </label>
            {cascadeSources.length > 0 && (
              <div className="space-y-1 mb-2">
                {cascadeSources.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-blue-500/5 border border-blue-500/20 text-xs">
                    <span className="text-[10px] text-blue-400 font-bold w-4">{i + 1}</span>
                    <span className="text-[var(--text-primary)] font-medium">{s.name}</span>
                    {s.vendor && <span className="text-[var(--text-muted)]">{s.vendor}</span>}
                    <button onClick={() => setCascadeSources(prev => prev.filter((_, idx) => idx !== i))} className="ml-auto text-[var(--text-muted)] hover:text-red-400"><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={srcName}
                onChange={e => setSrcName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSource()}
                placeholder="Source name"
                className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500"
              />
              <input
                value={srcVendor}
                onChange={e => setSrcVendor(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSource()}
                placeholder="Vendor"
                className="w-24 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500"
              />
              <button onClick={addSource} className="px-2 py-1.5 text-xs rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">Add</button>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Any additional context..."
              className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save Benchmark
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Latency Editor Modal ────────────────────────────────────────────

function LatencyEditor({ countryCode, onClose }: { countryCode: string; onClose: () => void }) {
  const [sourceName, setSourceName] = useState('')
  const [vendor, setVendor] = useState('')
  const [p95Min, setP95Min] = useState('')
  const [p95Max, setP95Max] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!sourceName.trim() || !vendor.trim() || !p95Min || !p95Max) {
      showToast('All fields required', 'error')
      return
    }
    setSaving(true)
    try {
      await matchRateApi.saveSourceLatency({
        source_name: sourceName.trim(),
        vendor: vendor.trim(),
        country_code: countryCode,
        p95_min_seconds: parseFloat(p95Min),
        p95_max_seconds: parseFloat(p95Max),
      })
      showToast('Latency saved', 'success')
      onClose()
      window.location.reload()
    } catch {
      showToast('Failed to save latency', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] shadow-2xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Add Source Latency — {countryCode}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-muted)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Source Name</label>
              <input value={sourceName} onChange={e => setSourceName(e.target.value)} placeholder="e.g. Equifax" className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Vendor</label>
              <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g. Datazoo" className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">P95 Min (seconds)</label>
              <input type="number" step="0.1" value={p95Min} onChange={e => setP95Min(e.target.value)} placeholder="0.5" className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">P95 Max (seconds)</label>
              <input type="number" step="0.1" value={p95Max} onChange={e => setP95Max(e.target.value)} placeholder="1.5" className="w-full px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2 text-xs font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save Latency
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Expert Panel ────────────────────────────────────────────────────

function ExpertPanel({ expert, onEdit }: { expert: ExpertOverrideData; countryCode: string; onEdit: () => void }) {
  const { user } = useAuthStore()
  const isAdmin = user?.isAdmin

  // Build AssessmentInfo from expert data
  const assessment: AssessmentInfo = {
    achievable: expert.two_plus_two_achievable || expert.one_plus_one_achievable,
    strength: expert.strength,
    strength_label: expert.strength_label,
    sources: expert.sources.map(s => ({
      name: s.name,
      vendor: s.vendor,
      coverage: s.coverage ?? 0,
      consent_required: s.consent_required,
    })),
    required_pii: expert.required_pii,
    any_consent_free: expert.sources.some(s => !s.consent_required),
    warnings: expert.warnings,
  }

  const primaryLabel = expert.two_plus_two_achievable ? '2+2' : expert.one_plus_one_achievable ? '1+1' : null

  return (
    <div className="bg-[var(--bg-primary)] rounded-lg border border-amber-500/30 p-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Crown size={14} className="text-amber-400" />
            <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Expert-Curated Recommendation</h4>
          </div>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
            Manually verified by the KYC team
            {expert.curated_by && <> &middot; {expert.curated_by}</>}
            {expert.updated_at && <> &middot; {new Date(expert.updated_at + 'Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors border border-amber-500/20"
          >
            <Pencil size={10} /> Edit
          </button>
        )}
      </div>

      {expert.summary && (
        <p className="text-xs text-[var(--text-secondary)] mb-3 leading-relaxed italic">{expert.summary}</p>
      )}

      <div className="space-y-3">
        {/* Ruleset badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {primaryLabel && (
            <span className={`px-2 py-0.5 text-xs rounded font-medium ${rulesetBadgeColor(primaryLabel, expert.strength)}`}>
              {primaryLabel} achievable — {expert.strength_label} ({expert.strength}%)
            </span>
          )}
          {!primaryLabel && (
            <span className="px-2 py-0.5 text-xs rounded font-medium bg-red-500/10 text-red-400">
              Neither 2+2 nor 1+1 achievable
            </span>
          )}
        </div>

        {/* Sources table */}
        {assessment.sources.length > 0 && (
          <div>
            <p className="text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Recommended cascade ({primaryLabel}):
            </p>
            <div className="rounded border border-[var(--border-color)] overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-amber-500/5">
                    <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase w-6">#</th>
                    <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Source</th>
                    <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Vendor</th>
                    <th className="text-right px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Coverage</th>
                    <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Flags</th>
                    <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {expert.sources.map((s, i) => (
                    <tr key={i} className="border-t border-[var(--border-color)]/50">
                      <td className="px-2 py-1.5 text-[var(--text-muted)]">{i + 1}</td>
                      <td className="px-2 py-1.5 text-[var(--text-primary)] font-medium">{s.name}</td>
                      <td className="px-2 py-1.5 text-[var(--text-secondary)]">{s.vendor}</td>
                      <td className="px-2 py-1.5 text-right">
                        {s.coverage != null && s.coverage > 0 ? (
                          <span className="text-[var(--text-primary)]">{s.coverage}%</span>
                        ) : (
                          <span className="text-[var(--text-muted)]">&mdash;</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1 flex-wrap">
                          {s.consent_required && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">CONSENT</span>}
                          {s.government_id && <span className="text-[10px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-400">GOV-ID</span>}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-[var(--text-muted)] max-w-[200px] truncate" title={s.reason || ''}>
                        {s.reason || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Required PII */}
        {expert.required_pii.length > 0 && (
          <div>
            <p className="text-xs font-medium text-[var(--text-secondary)] mb-1">Required data:</p>
            <div className="flex gap-1 flex-wrap">
              {expert.required_pii.map((pii, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
                  {pii}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Expert notes */}
        {expert.notes && (
          <div className="p-2.5 rounded bg-amber-500/5 border border-amber-500/15">
            <p className="text-[10px] font-medium text-amber-400 mb-1">Expert notes:</p>
            <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">{expert.notes}</p>
          </div>
        )}

        {/* Warnings */}
        <WarningsList warnings={expert.warnings} />
      </div>
    </div>
  )
}

// ─── Expert Override Editor Modal ─────────────────────────────────────

interface AvailableSource {
  name: string
  vendor: string
  coverage: number | null
  consent_required: boolean
  government_id: boolean
  production_pass_rate?: number | null
  production_checks?: number | null
}

function ExpertOverrideEditor({ countryCode, ruleset, existingData, availableSources, onClose, onSaved }: {
  countryCode: string
  ruleset: string
  existingData: ExpertOverrideData | null
  availableSources: AvailableSource[]
  onClose: () => void
  onSaved: (expert: ExpertOverrideData) => void
}) {
  // Selected sources in cascade order
  const [selected, setSelected] = useState<AvailableSource[]>(() => {
    if (existingData?.sources.length) {
      // Match existing expert sources against available sources to get full data
      return existingData.sources.map(es => {
        const match = availableSources.find(a => a.name === es.name && a.vendor === es.vendor)
        return match || { name: es.name, vendor: es.vendor, coverage: es.coverage, consent_required: es.consent_required, government_id: es.government_id }
      })
    }
    return []
  })
  const [notes, setNotes] = useState(existingData?.notes ?? '')
  const [saving, setSaving] = useState(false)
  const { user } = useAuthStore()
  const [customName, setCustomName] = useState('')
  const [customVendor, setCustomVendor] = useState('')

  // Deduplicate available sources by name+vendor
  const deduped = availableSources.filter((s, i, arr) =>
    arr.findIndex(a => a.name === s.name && a.vendor === s.vendor) === i
  )
  const unselected = deduped.filter(a => !selected.some(s => s.name === a.name && s.vendor === a.vendor))

  const addSource = (src: AvailableSource) => setSelected(prev => [...prev, src])
  const removeSource = (i: number) => setSelected(prev => prev.filter((_, idx) => idx !== i))
  const moveUp = (i: number) => {
    if (i === 0) return
    setSelected(prev => { const next = [...prev]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; return next })
  }
  const moveDown = (i: number) => {
    if (i >= selected.length - 1) return
    setSelected(prev => { const next = [...prev]; [next[i], next[i + 1]] = [next[i + 1], next[i]]; return next })
  }

  const handleSave = async () => {
    if (selected.length === 0) { showToast('Select at least one source', 'error'); return }
    setSaving(true)
    try {
      // Auto-derive: strength = min coverage of selected, achievability from source count
      const coverages = selected.map(s => s.coverage).filter((c): c is number => c != null && c > 0)
      const minCoverage = coverages.length > 0 ? Math.min(...coverages) : 0
      const uniqueVendors = new Set(selected.map(s => s.vendor))
      const twoAchievable = uniqueVendors.size >= 2
      const oneAchievable = selected.length >= 1
      // Collect all mandatory PII from the sources
      const allPii = new Set<string>()
      for (const s of selected) {
        if (s.government_id) allPii.add('National ID')
        if (s.consent_required) allPii.add('Consent')
      }

      const sources = selected.map(s => ({
        name: s.name,
        vendor: s.vendor,
        coverage: s.coverage,
        consent_required: s.consent_required,
        government_id: s.government_id,
        reason: null,
      }))
      const summary = selected.map(s => s.name).join(' → ')
      const strengthLabel = minCoverage >= 80 ? 'strong' : minCoverage >= 50 ? 'decent' : minCoverage > 0 ? 'weak' : 'not_achievable'

      await matchRateApi.saveExpertOverride({
        country_code: countryCode,
        ruleset,
        sources,
        two_plus_two_achievable: twoAchievable,
        one_plus_one_achievable: oneAchievable,
        strength: minCoverage,
        required_pii: [...allPii],
        warnings: [],
        notes: notes.trim() || null,
        summary,
      })

      // Build ExpertOverrideData to update UI immediately
      const expertData: ExpertOverrideData = {
        sources,
        two_plus_two_achievable: twoAchievable,
        one_plus_one_achievable: oneAchievable,
        strength: minCoverage,
        strength_label: strengthLabel,
        required_pii: [...allPii],
        warnings: [],
        notes: notes.trim() || null,
        summary,
        curated_by: user?.email || null,
        updated_at: new Date().toISOString(),
        status: 'curated',
      }

      showToast(`Expert override saved for ${countryCode}`, 'success')
      onSaved(expertData)
      onClose()
    } catch {
      showToast('Failed to save expert override', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-amber-500/30 shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto mx-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Crown size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Set Expert Cascade — {countryCode}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-muted)]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-[var(--text-muted)]">
            Pick the sources and set the order. The system will auto-derive achievability, strength, and PII from the source data.
          </p>

          {/* Selected cascade (ordered) */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-2 block">
              Cascade order ({selected.length} source{selected.length !== 1 ? 's' : ''})
            </label>
            {selected.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)] py-3 text-center border border-dashed border-[var(--border-color)] rounded-lg">
                Click sources below to add them to the cascade
              </p>
            ) : (
              <div className="space-y-1">
                {selected.map((s, i) => (
                  <div key={`${s.name}-${s.vendor}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <span className="text-[10px] text-amber-400 font-bold w-4 shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[var(--text-primary)]">{s.name}</span>
                      <span className="text-[10px] text-[var(--text-muted)] ml-1.5">{s.vendor}</span>
                      {s.coverage != null && s.coverage > 0 && (
                        <span className="text-[10px] text-[var(--text-muted)] ml-1.5">{s.coverage}%</span>
                      )}
                      {s.production_checks != null && s.production_checks > 0 && (
                        <span className="text-[10px] text-cyan-400 ml-1.5">{s.production_pass_rate?.toFixed(0)}% prod</span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {s.consent_required && <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">C</span>}
                      {s.government_id && <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-400">ID</span>}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => moveUp(i)} disabled={i === 0} className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"><ChevronUp size={12} /></button>
                      <button onClick={() => moveDown(i)} disabled={i === selected.length - 1} className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"><ChevronDown size={12} /></button>
                      <button onClick={() => removeSource(i)} className="p-0.5 text-[var(--text-muted)] hover:text-red-400"><X size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available sources to pick from */}
          {unselected.length > 0 && (
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] mb-2 block">
                Available sources
              </label>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {unselected.map(s => (
                  <button
                    key={`${s.name}-${s.vendor}`}
                    onClick={() => addSource(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] hover:border-amber-500/30 transition-colors text-left"
                  >
                    <Plus size={10} className="text-amber-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[var(--text-primary)]">{s.name}</span>
                      <span className="text-[10px] text-[var(--text-muted)] ml-1.5">{s.vendor}</span>
                    </div>
                    {s.coverage != null && s.coverage > 0 && (
                      <span className="text-[10px] text-[var(--text-secondary)] shrink-0">{s.coverage}%</span>
                    )}
                    {s.production_checks != null && s.production_checks > 0 && (
                      <span className="text-[10px] text-cyan-400 shrink-0">{s.production_pass_rate?.toFixed(0)}% prod</span>
                    )}
                    <div className="flex items-center gap-0.5 shrink-0">
                      {s.consent_required && <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">C</span>}
                      {s.government_id && <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-400">ID</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add custom source */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Add custom source</label>
            <div className="flex items-center gap-2">
              <input
                value={customName} onChange={e => setCustomName(e.target.value)}
                placeholder="Source name"
                className="flex-1 px-2.5 py-1.5 text-xs rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] outline-none focus:border-amber-500/50"
                onKeyDown={e => {
                  if (e.key === 'Enter' && customName.trim() && customVendor.trim()) {
                    addSource({ name: customName.trim(), vendor: customVendor.trim(), coverage: null, consent_required: false, government_id: false })
                    setCustomName(''); setCustomVendor('')
                  }
                }}
              />
              <input
                value={customVendor} onChange={e => setCustomVendor(e.target.value)}
                placeholder="Vendor"
                className="flex-1 px-2.5 py-1.5 text-xs rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] outline-none focus:border-amber-500/50"
                onKeyDown={e => {
                  if (e.key === 'Enter' && customName.trim() && customVendor.trim()) {
                    addSource({ name: customName.trim(), vendor: customVendor.trim(), coverage: null, consent_required: false, government_id: false })
                    setCustomName(''); setCustomVendor('')
                  }
                }}
              />
              <button
                onClick={() => {
                  if (!customName.trim() || !customVendor.trim()) return
                  addSource({ name: customName.trim(), vendor: customVendor.trim(), coverage: null, consent_required: false, government_id: false })
                  setCustomName(''); setCustomVendor('')
                }}
                disabled={!customName.trim() || !customVendor.trim()}
                className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-30 shrink-0"
              >
                <Plus size={10} /> Add
              </button>
            </div>
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">Notes (optional)</label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Any context or reasoning..."
              className="w-full px-3 py-1.5 text-xs rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[var(--bg-secondary)] border-t border-[var(--border-color)] px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-xs rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || selected.length === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Crown size={12} />}
            Save Expert Cascade
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Shared Components ───────────────────────────────────────────────────

function PreferButton({ tab, preferred, onSetPreferred }: { tab: RecommendationTab; preferred?: RecommendationTab; onSetPreferred: (tab: RecommendationTab) => void }) {
  const isPreferred = preferred === tab
  return (
    <div className="flex items-center justify-end">
      <button
        onClick={() => onSetPreferred(tab)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
          isPreferred
            ? 'bg-violet-600 text-white'
            : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-violet-500/10 hover:text-violet-400 border border-[var(--border-color)]'
        }`}
      >
        <CheckCircle size={12} />
        {isPreferred ? 'Preferred recommendation' : 'Use this recommendation'}
      </button>
    </div>
  )
}

// ─── Cascade Panel (shared by Spreadsheet & Production) ──────────────

function CascadePanel({
  title, subtitle, assessment11, assessment22, sources, warnings, recommendedVendor, status, showProductionStats,
}: {
  title: string
  subtitle: string
  assessment11: AssessmentInfo | null
  assessment22: AssessmentInfo | null
  sources: SourceInfo[]
  warnings: string[]
  recommendedVendor: string | null
  status: string
  showProductionStats?: boolean
}) {
  // Tone down green: only use green border for strong achievable (>60%)
  const primaryStrength = assessment22?.achievable ? assessment22.strength : assessment11?.achievable ? assessment11.strength : null
  const borderColor = status === 'success' && primaryStrength != null && primaryStrength > 60
    ? 'border-green-500/20'
    : status === 'success' || status === 'warning' ? 'border-amber-500/20'
    : 'border-[var(--border-color)]'
  const has22 = assessment22?.achievable
  const has11 = assessment11?.achievable
  const primary = has22 ? assessment22! : has11 ? assessment11! : null
  const primaryLabel = has22 ? '2+2' : has11 ? '1+1' : null
  const fallbackLabel = has22 && has11 ? '1+1' : null
  const fallback = has22 && has11 ? assessment11! : null

  return (
    <div className={`bg-[var(--bg-primary)] rounded-lg border ${borderColor} p-3`}>
      <div className="mb-3">
        <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{title}</h4>
        <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>
      </div>

      {!primary && sources.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">No data available.</p>
      ) : (
        <div className="space-y-3">
          {/* Ruleset badges */}
          <div className="flex items-center gap-2 flex-wrap">
            {primaryLabel && (
              <span className={`px-2 py-0.5 text-xs rounded font-medium ${rulesetBadgeColor(primaryLabel, primary!.strength)}`}>
                {primaryLabel} achievable — {primary!.strength_label} ({primary!.strength}%)
              </span>
            )}
            {fallbackLabel && fallback && (
              <span className="px-2 py-0.5 text-xs rounded font-medium bg-blue-500/10 text-blue-400">
                {fallbackLabel} fallback — {fallback.strength}%
              </span>
            )}
            {!primaryLabel && (
              <span className="px-2 py-0.5 text-xs rounded font-medium bg-red-500/10 text-red-400">
                Neither 2+2 nor 1+1 achievable
              </span>
            )}
            {recommendedVendor && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-violet-500/10 text-violet-400">
                Rec: {recommendedVendor}
              </span>
            )}
          </div>

          {/* Recommended cascade table */}
          {primary && primary.sources.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Recommended cascade ({primaryLabel}):
              </p>
              <div className="rounded border border-[var(--border-color)] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--bg-tertiary)]">
                      <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase w-6">#</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Source</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Vendor</th>
                      <th className="text-right px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Coverage</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-medium text-[var(--text-muted)] uppercase">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {primary.sources.map((s, i) => {
                      const fullSource = sources.find(fs => fs.name === s.name && fs.vendor === s.vendor)
                      const isInProduction = fullSource?.production_checks != null && fullSource.production_checks > 0
                      return (
                        <tr key={i} className={`border-t border-[var(--border-color)]/50 ${isInProduction ? 'bg-cyan-500/[0.03]' : ''}`}>
                          <td className="px-2 py-1.5 text-[var(--text-muted)]">{i + 1}</td>
                          <td className="px-2 py-1.5 font-medium">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[var(--text-primary)]">{s.name}</span>
                              {isInProduction && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-400 border border-cyan-500/25 font-medium" title={`Live in Kraken — ${fullSource!.production_checks!.toLocaleString()} checks`}>
                                  <Zap size={8} className="fill-cyan-400" /> LIVE
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-[var(--text-secondary)]">{s.vendor}</td>
                          <td className="px-2 py-1.5 text-right">
                            {s.coverage > 0 ? (
                              <span className="text-[var(--text-primary)]">{s.coverage}%</span>
                            ) : (
                              <span className="text-[var(--text-muted)]">—</span>
                            )}
                            {showProductionStats && fullSource?.production_pass_rate != null && (
                              <span className="text-cyan-400 ml-1">({fullSource.production_pass_rate.toFixed(0)}% prod)</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex gap-1 flex-wrap">
                              {isInProduction && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-medium" title={`${fullSource!.production_checks!.toLocaleString()} checks in Kraken`}>
                                  {fullSource!.production_checks!.toLocaleString()} checks
                                </span>
                              )}
                              {s.consent_required && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">CONSENT</span>}
                              {fullSource?.government_id && <span className="text-[10px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-400">GOV-ID</span>}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Required PII */}
          {primary && primary.required_pii.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-secondary)] mb-1">Required data:</p>
              <div className="flex gap-1 flex-wrap">
                {primary.required_pii.map((pii, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
                    {pii}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Warnings (collapsed if many) */}
          <WarningsList warnings={[...(warnings || []), ...(primary?.warnings || [])]} />
        </div>
      )}
    </div>
  )
}

// ─── Warnings List (collapsible when many) ──────────────────────────────

function WarningsList({ warnings }: { warnings: string[] }) {
  const [expanded, setExpanded] = useState(false)
  // Deduplicate warnings
  const unique = [...new Set(warnings)]
  if (unique.length === 0) return null

  const VISIBLE_COUNT = 3
  const visible = expanded ? unique : unique.slice(0, VISIBLE_COUNT)
  const hiddenCount = unique.length - VISIBLE_COUNT

  return (
    <div className="space-y-1">
      {visible.map((w, i) => (
        <p key={i} className="text-[10px] text-amber-400 flex items-start gap-1">
          <AlertCircle size={10} className="shrink-0 mt-0.5" /> {w}
        </p>
      ))}
      {hiddenCount > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-[10px] text-amber-400/70 hover:text-amber-400 flex items-center gap-1 mt-0.5"
        >
          <ChevronDown size={10} /> {hiddenCount} more warning{hiddenCount === 1 ? '' : 's'}...
        </button>
      )}
      {expanded && unique.length > VISIBLE_COUNT && (
        <button
          onClick={() => setExpanded(false)}
          className="text-[10px] text-amber-400/70 hover:text-amber-400 flex items-center gap-1 mt-0.5"
        >
          <ChevronUp size={10} /> Show less
        </button>
      )}
    </div>
  )
}

// ─── Brain Section (collapsible) ──────────────────────────────────────

function BrainSection({ brain }: { brain: AIResult }) {
  const [open, setOpen] = useState(false)
  if (brain.status === 'skipped') return null
  return (
    <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--text-secondary)]">Frankie Brain</span>
          <StatusDot label="" status={brain.status} />
        </div>
        {open ? <ChevronUp size={14} className="text-[var(--text-muted)]" /> : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
      </button>
      {open && (
        <div className="border-t border-[var(--border-color)] p-3">
          {brain.status === 'success' ? (
            <div className="text-xs text-[var(--text-primary)] whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed">
              {brain.recommendation}
            </div>
          ) : (
            <p className="text-xs text-red-400">{brain.error || 'No response'}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Shared Components ───────────────────────────────────────────────────

function StatusDot({ label, status }: { label: string; status: string }) {
  const Icon = status === 'success' ? CheckCircle : status === 'error' ? XCircle : status === 'warning' ? AlertCircle : MinusCircle
  const color = status === 'success' ? 'text-green-400' : status === 'error' ? 'text-red-400' : status === 'warning' ? 'text-amber-400' : 'text-[var(--text-muted)]'
  return (
    <span className={`flex items-center gap-1 text-[10px] ${color}`} title={label}>
      <Icon size={10} /> {label}
    </span>
  )
}


// ─── Deal Picker ─────────────────────────────────────────────────────────

function DealPicker() {
  const { selectedDeal, selectDeal, searchDeals, searchResults, isSearching } = useHomeStore()
  const [query, setQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (query.length < 2) { setShowResults(false); return }
    setShowResults(true)
    const timer = setTimeout(() => searchDeals(query), 300)
    return () => clearTimeout(timer)
  }, [query, searchDeals])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (deal: HubSpotDeal) => {
    selectDeal(deal)
    setQuery('')
    setShowResults(false)
  }

  return (
    <div className="relative" ref={wrapperRef}>
      {selectedDeal ? (
        /* Deal selected — just show the chip */
        <div className="flex items-center min-h-[42px] p-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)]">
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
            <Building2 size={11} />
            <span className="font-medium truncate max-w-[300px]">{selectedDeal.name}</span>
            {selectedDeal.companyName && (
              <span className="text-violet-400/60 hidden sm:inline">— {selectedDeal.companyName}</span>
            )}
            <button
              onClick={() => selectDeal(null)}
              className="hover:text-red-400 ml-0.5"
              title="Unlink deal"
            >
              <X size={11} />
            </button>
          </span>
        </div>
      ) : (
        /* No deal — show search input */
        <div className="flex items-center gap-1.5 min-h-[42px] p-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)]">
          {isSearching ? (
            <Loader2 size={13} className="text-[var(--text-muted)] animate-spin shrink-0" />
          ) : (
            <Search size={13} className="text-[var(--text-muted)] shrink-0" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if (query.length >= 2) setShowResults(true) }}
            placeholder="Search HubSpot deals..."
            className="flex-1 bg-transparent text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
          />
        </div>
      )}

      {/* Results dropdown */}
      {showResults && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-xl">
          {isSearching ? (
            <div className="px-4 py-6 text-center">
              <Loader2 size={16} className="text-violet-400 animate-spin mx-auto mb-1" />
              <p className="text-xs text-[var(--text-muted)]">Searching HubSpot...</p>
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map(deal => (
              <button
                key={deal.id}
                onClick={() => handleSelect(deal)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)]/50 last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[var(--text-primary)] truncate">{deal.name}</div>
                  <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-2 mt-0.5">
                    {deal.companyName && (
                      <span className="flex items-center gap-1"><Building2 size={10} /> {deal.companyName}</span>
                    )}
                    <span className="flex items-center gap-1"><User size={10} /> {deal.ownerName}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {deal.amount != null && (
                    <div className="text-xs font-medium text-[var(--text-primary)]">
                      ${deal.amount.toLocaleString()}
                    </div>
                  )}
                  <div className="text-[10px] text-[var(--text-muted)]">{deal.stage}</div>
                </div>
              </button>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-xs text-[var(--text-muted)]">
              No deals found for &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  )
}
